import { Card, CardContent, Radio, Stack, useTheme } from '@mui/joy';
import { Warning } from '@phosphor-icons/react';
import { t } from 'i18next';
import { Control, FieldErrors } from 'react-hook-form';

import { FormField } from 'core/components';
import { InputType } from 'core/enums';
// import { theme } from 'theme';
import { Color } from 'theme/enums';

import { FreightPriceType } from './enums';
import { IChangeVersionDataForm } from './types';

interface IProps {
  control: Control<IChangeVersionDataForm>;
  errors: FieldErrors<IChangeVersionDataForm>;
  choice: { value: number; label: string };
  priceTypeValue: FreightPriceType;
  onPriceChange: (value: number) => void;
  onMarginChange: (value: number) => void;
  onPercentageChange: (value: number) => void;
  priceWarning: string;
  percentageWarning: string;
}

export const FreightPriceChoice = ({
  control,
  errors,
  choice,
  priceTypeValue,
  onPriceChange,
  onMarginChange,
  onPercentageChange,
  priceWarning,
  percentageWarning,
}: IProps) => {
  const theme = useTheme();

  const getFreightPriceChoiceContent = (choice) => {
    switch (choice.value) {
      case FreightPriceType.PRICE:
        return (
          <Stack spacing={1}>
            <FormField
              name="freightPrice"
              type={InputType.NUMBER_INPUT}
              inputProps={{
                label: t('labelFixedFreightPrice'),
                placeholder: t('labelFixedFreightPrice'),
                control: control,
                error: errors.freightPrice,
                color: priceWarning
                  ? Color.WARNING
                  : errors?.freightPrice
                  ? Color.DANGER
                  : Color.NEUTRAL,
                startDecorator: priceWarning ? <Warning weight="fill" /> : null,
                onChange: ({ target: { value }, source }) => {
                  if (source !== 'event') {
                    return;
                  }

                  onPriceChange(Number(value));
                },
                slotProps: {
                  input: {
                    thousandSeparator: true,
                    prefix: '$',
                    sx: { position: 'relative', zIndex: 2 },
                  },
                },
                disabled: FreightPriceType.PRICE !== priceTypeValue,
                helperTexts: [priceWarning],
                helperTextStyle: {
                  color: theme.palette.warning[400],
                },
                wrapperStyle: { fontSize: 14 },
              }}
            />

            <FormField
              name="freightMargin"
              type={InputType.NUMBER_INPUT}
              inputProps={{
                label: t('labelMarginOnFreight'),
                placeholder: t('labelMarginOnFreight'),
                control: control,
                error: errors.freightMargin,
                onChange: ({ target: { value }, source }) => {
                  if (source !== 'event') {
                    return;
                  }

                  onMarginChange(Number(value));
                },
                slotProps: {
                  input: {
                    max: 100,
                    decimalScale: 2,
                    allowNegative: true,
                    suffix: '%',
                    sx: { position: 'relative', zIndex: 2 },
                  },
                },
                disabled: FreightPriceType.PRICE !== priceTypeValue,
                helperTexts: [t('helperTextMargin')],
                wrapperStyle: { fontSize: 14 },
              }}
            />
          </Stack>
        );
      case FreightPriceType.PERCENTAGE:
        return (
          <FormField
            name="freightPercentage"
            type={InputType.NUMBER_INPUT}
            inputProps={{
              label: t('labelPercentageOnFreight'),
              placeholder: t('labelPercentageOnFreight'),
              control: control,
              error: errors.freightPercentage,
              color: percentageWarning
                ? Color.WARNING
                : errors.freightPercentage
                ? Color.DANGER
                : Color.NEUTRAL,
              startDecorator: percentageWarning ? <Warning weight="fill" /> : null,
              onChange: ({ target: { value }, source }) => {
                if (source !== 'event') {
                  return;
                }

                onPercentageChange(Number(value));
              },
              slotProps: {
                input: {
                  decimalScale: 2,
                  allowNegative: true,
                  suffix: '%',
                  sx: { position: 'relative', zIndex: 2 },
                },
              },
              disabled: FreightPriceType.PERCENTAGE !== priceTypeValue,
              helperTexts: [percentageWarning],
              helperTextStyle: {
                color: theme.palette.warning[400],
              },
              wrapperStyle: { fontSize: 14 },
            }}
          />
        );
      default:
        return choice.label;
    }
  };

  return (
    <Card>
      <CardContent>
        <Radio
          value={choice.value}
          label={getFreightPriceChoiceContent(choice)}
          sx={{ alignItems: 'center' }}
        />
      </CardContent>
    </Card>
  );
};
